import React, { useState } from 'react';
import ZentorgaForm from '../components/ZentorgaForm';
import SurveyQuestion from '../components/SurveyQuestion';
import BenefitPeriod from '../../components/BenefitPeriod';
import KeepButton from '../components/KeepButton';
import SoundVideo from '../components/SoundVideo';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { useMultiStep } from '../hooks/useMultiStep';
import { getImagesByStep } from '../utils/templateMapper';
import '../styles/zentorga.scss';
import '../styles/template22.scss';

/**
 * Template22 - 두 단계 다단계 폼 (고유한 레이아웃)
 * 구조:
 * Step 1: 이미지(1개만) → Keep 버튼
 * Step 2: 이미지(5개) → 설문(1개) → 혜택기간 → 폼 → 서비스정보 → 푸터
 */
const Template22 = ({
  landingData,
  urlCode,
  urlCodeSettingId
}) => {
  const { currentStep, goToNextStep } = useMultiStep(2);
  const { handleSubmit, isSubmitting } = useFormSubmit(
    urlCode,
    urlCodeSettingId,
    landingData.endpoint
  );
  const [surveyAnswer, setSurveyAnswer] = useState('');

  const handleFormSubmit = async (formData) => {
    await handleSubmit({
      ...formData,
      ...(surveyAnswer && { selectedOption: surveyAnswer }),
    });
  };

  const stepImages = getImagesByStep(landingData, 2);

  const renderMediaContent = (img) => {
    const fileExtension = img.url.split('.').pop().toLowerCase();
    const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);

    return isVideo ? (
      <SoundVideo src={img.url} loop={img.alt !== "false"} />
    ) : (
      <img src={img.url} alt={img.alt} />
    );
  };

  return (
    <section className="template22-layout zentorga-layout">
      {/* Step 1: Single Image + Keep Button */}
      {currentStep === 0 && (
        <div className="step step1-container">
          {/* Step 1 Images */}
          {stepImages.step1.length > 0 && (
            <div className="step1-image-container zentorga-images">
              {stepImages.step1.map((img, idx) => (
                <div key={idx}>
                  {renderMediaContent(img)}
                </div>
              ))}
            </div>
          )}

          {/* Keep Button */}
          <KeepButton
            onClick={() => goToNextStep()}
            imageUrl={landingData.keepBtnUrl}
            text={landingData.keepBtnText || '계속하기'}
          />
        </div>
      )}

      {/* Step 2: Images + Survey + Benefit Period + Form */}
      {currentStep === 1 && (
        <div className="step step2-container">
          {/* Step 2 Images */}
          {stepImages.step2.length > 0 && (
            <div className="step2-images zentorga-images">
              {stepImages.step2.map((img, idx) => (
                <div key={idx}>
                  {renderMediaContent(img)}
                </div>
              ))}
            </div>
          )}

          {/* Survey Section */}
          {landingData.surveyTitle1 && (
            <div className="surveySection">
              <SurveyQuestion
                topic={landingData.surveyTitle1}
                answers={[
                  landingData.surveyOpt1_1,
                  landingData.surveyOpt1_2,
                  landingData.surveyOpt1_3,
                  landingData.surveyOpt1_4,
                  landingData.surveyOpt1_5
                ].filter(Boolean)}
                questionNumber={1}
                displayType={landingData.surveyTopic?.topic?.[0]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
                onChange={setSurveyAnswer}
              />
            </div>
          )}

          {/* 이벤트 대상자 */}
          {landingData.eventTarget && (
            <div className="eventTarget">
              <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
            </div>
          )}

          {/* Benefit Period */}
          {landingData.benefitDate && (
            <div className="benefitDate">
              <BenefitPeriod periodType={landingData.benefitDate} />
            </div>
          )}

          {/* Form Section */}
          <div className="formStyleClassName">
            <ZentorgaForm
              onSubmit={handleFormSubmit}
          isSubmitting={isSubmitting}
              formStyle={landingData.formStyleClassName || 'gray'}
              consentStyle={landingData.privacyPolicyClassName || 'dark'}
              privacyPolicyName={landingData.privacyPolicyName || '본원'}
              privacyPolicy={landingData.privacyPolicy}
              marketingPolicy={landingData.marketingPolicy}
              buttonComponent={
                <button
                  type="submit"
                  className={`postBtn ${landingData.postBtnLocate === 'fixed' ? 'postBtn-fixed' : ''}`}
                  id="postBtn"
                  disabled={isSubmitting}
                >
                  {landingData.postBtnUrl ? (
                    <img src={landingData.postBtnUrl} alt="신청하기" />
                  ) : (
                    <span>{isSubmitting ? '신청 중...' : '신청하기'}</span>
                  )}
                </button>
              }
            />
          </div>

          {/* Service Info */}
          {landingData.serviceInfoClassName && (
            <p
              className={`serviceInfoClassName ${landingData.serviceInfoClassName}`}
              id="serviceInfoClassName"
            >
              * 본 서비스는 전국 20여 개 이상의 제휴 병원에서 제공되며,<br />
              상담 신청 정보를 기반으로 전문 상담원이 적합한 제휴 병원을 안내해드립니다.
            </p>
          )}
        </div>
      )}

      {/* Footer (outside steps) */}
      {landingData.footerClassName !== null && (landingData.footerClassName !== undefined || landingData.footerDetail) && (
        <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
          {landingData.footerDetail && (
            <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
              {landingData.footerDetail}
            </p>
          )}
        </footer>
      )}
    </section>
  );
};

export default Template22;
